import {
  type SelectroV2Props,
  type SelectroInlineItem,
} from '@aha-app/selectro-v2';
import terminology from 'javascripts/terminology';
import ItemFilter, { type FilterOption } from './ItemFilter';

type RecordTypes = 'Epic' | 'Feature' | 'Requirement';

class RecordTypeFilter extends ItemFilter<string> {
  /**
   * @param initialItems values initially selected by the filter
   * @param recordTypes an array of record types (e.g. Epic, Feature, Requirement)
   * @param applyTerminology whether to apply terminology to the record types, defaults true
   * @param pluralTerminology when applying terminology, whether to use plural forms defaults true
   */
  constructor({
    initialItems,
    recordTypes = ['Epic', 'Feature', 'Requirement'],
    applyTerminology = true,
    pluralTerminology = true,
  }: {
    initialItems: string[];
    recordTypes?: RecordTypes[];
    applyTerminology?: boolean;
    pluralTerminology?: boolean;
  }) {
    const initialOptions = RecordTypeFilter.initOptions({
      recordTypes,
      applyTerminology,
      pluralTerminology,
    });

    super(initialItems, initialOptions);
  }

  static initOptions({
    recordTypes,
    applyTerminology = true,
    pluralTerminology = true,
  }: {
    recordTypes: RecordTypes[];
    applyTerminology?: boolean;
    pluralTerminology?: boolean;
  }): FilterOption<string>[] {
    return recordTypes.map(t => {
      return {
        text:
          (applyTerminology
            ? terminology(t, { isPlural: pluralTerminology })
            : t) ?? t,
        value: t,
      };
    });
  }

  /*
   * Override parent, allowing an "Any" option, this is useful as a default for the SelectSingle component
   */
  selectroItems({
    withAny = true,
  }: { withAny?: boolean } = {}): SelectroInlineItem[] {
    return [
      ...(withAny ? [{ text: 'Any', value: '' }] : []),
      ...super.selectroItems(),
    ];
  }

  /**
   * Returns true if the record is matched by this filter, false
   * otherwise. The default is to match all records if no
   * selectedItems are defined.
   */
  matches(record: unknown) {
    return !!(
      super.matches(record) ||
      (record &&
        typeof record === 'object' &&
        'typename' in record &&
        typeof record.typename === 'string' &&
        this.itemIsSelected(record.typename))
    );
  }

  static getSelectroProps(): SelectroV2Props {
    return {
      text: {
        preScript: 'Record type: ',
        noneSelected: 'Any',
      },
    };
  }
}

export default RecordTypeFilter;
